import { useUser } from '@myosh/myosh-login';
import { useStoredValue } from '@myosh/odin-components';
import { useCallback, useMemo } from 'react';
import { PINNED_COLUMN_IDS } from '@/components/schedules/columns';

export interface ColumnSettings {
  visibility: Record<string, boolean>;
  pinned: string[];
}

export const DEFAULT_COLUMN_SETTINGS: ColumnSettings = { visibility: {}, pinned: PINNED_COLUMN_IDS };

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isVisibilityMap = (value: unknown): value is Record<string, boolean> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((visible) => typeof visible === 'boolean');

export const parseColumnSettings = (stored: string): ColumnSettings => {
  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed === 'object' && parsed !== null && 'visibility' in parsed && 'pinned' in parsed) {
      const { visibility, pinned } = parsed;
      if (isVisibilityMap(visibility) && isStringArray(pinned)) {
        return { visibility, pinned };
      }
    }
  } catch {
    // Unreadable settings fall back to the defaults below.
  }
  return DEFAULT_COLUMN_SETTINGS;
};

export const useColumnSettings = () => {
  const { state } = useUser();
  const [stored, setStored] = useStoredValue({
    key: `scheduler:columns:${state.user?.preferred_username ?? ''}`,
    fromStored: (value) => value ?? '',
  });

  const settings = useMemo(() => (stored ? parseColumnSettings(stored) : DEFAULT_COLUMN_SETTINGS), [stored]);
  const saveSettings = useCallback((next: ColumnSettings) => setStored(JSON.stringify(next)), [setStored]);

  return { settings, saveSettings };
};
